import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { AmenityIcon } from "@/components/AmenityIcon";
import { MATERIAL_ICONS } from "@/components/MaterialIcons";
import { Plus, X, Trash2, Pencil, Search, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AdminAmenities() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id: "", nameEn: "", nameAr: "", icon: "Checkroom", type: "amenity", category: "Comfort" });
  const [iconDropdownOpen, setIconDropdownOpen] = useState(false);
  const [iconSearch, setIconSearch] = useState("");
  const iconDropdownRef = useRef(null);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (iconDropdownRef.current && !iconDropdownRef.current.contains(e.target)) {
        setIconDropdownOpen(false);
        setIconSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/amenities");
      setAmenities(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/amenities/${editing}`, { nameEn: form.nameEn, nameAr: form.nameAr, icon: form.icon, type: form.type, category: form.category });
        toast.success("Amenity updated");
      } else {
        await api.post("/admin/amenities", form);
        toast.success("Amenity created");
      }
      closeDialog();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(`Delete amenity "${id}"?`)) return;
    try {
      await api.delete(`/admin/amenities/${id}`);
      toast.success("Amenity deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ id: "", nameEn: "", nameAr: "", icon: "Checkroom", type: "amenity", category: "Comfort" });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a.id);
    setForm({ id: a.id, nameEn: a.nameEn, nameAr: a.nameAr, icon: a.icon, type: a.type, category: a.category });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const categories = ["Essential", "Match", "Comfort", "Pro"];

  const langName = (a) => i18n.language?.startsWith("ar") ? a.nameAr : a.nameEn;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground hover:text-volt transition-colors mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="label-eyebrow text-volt mb-2">{t("admin.eyebrow")}</div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Amenities & Services</h1>
          </div>
          <button
            onClick={openAdd}
            className="bg-volt text-black font-bold text-xs tracking-widest uppercase px-5 py-3 flex items-center gap-2 hover:bg-volt/90"
          >
            <Plus className="h-4 w-4" /> Add New
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-10 text-muted-foreground">Loading...</div>
        ) : (
          <div>
            {categories.map((cat) => {
              const items = amenities.filter((a) => a.category === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat} className="mb-6">
                  <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground mb-3">{cat}</div>
                  <div className="border border-border">
                    {items.map((a) => (
                      <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-border/30 last:border-b-0 hover:bg-muted/30">
                        <div className="flex items-center gap-3">
                          <AmenityIcon name={a.icon} className="h-4 w-4 text-muted-foreground/70" />
                          <div>
                            <div className="text-sm font-medium">{langName(a)}</div>
                            <div className="text-[10px] text-muted-foreground">{a.id} · {a.type}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEdit(a)} className="p-1.5 hover:bg-muted transition-colors">
                            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                          <button onClick={() => handleDelete(a.id)} className="p-1.5 hover:bg-red-500/10 transition-colors">
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Dialog ── */}
      {dialogOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) closeDialog(); }}
        >
          <div className="bg-card border border-border w-full max-w-lg rounded-sm shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="text-volt text-[10px] tracking-widest uppercase font-bold">
                {editing ? "Edit Amenity" : "Add Amenity"}
              </div>
              <button onClick={closeDialog} className="text-muted-foreground hover:text-foreground transition-colors p-1">
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">ID (slug) *</label>
                <input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} disabled={!!editing}
                  className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt disabled:opacity-50" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">Name (EN) *</label>
                  <input value={form.nameEn} onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                    className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt" required />
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">Name (AR) *</label>
                  <input value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} dir="rtl"
                    className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt" required />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">Icon</label>
                  <div className="relative" ref={iconDropdownRef}>
                    <button type="button" onClick={() => { setIconDropdownOpen(!iconDropdownOpen); setIconSearch(""); }}
                      className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt flex items-center gap-2 text-left">
                      <AmenityIcon name={form.icon} className="h-4 w-4 text-muted-foreground" />
                      <span>{form.icon}</span>
                      <span className="ml-auto text-muted-foreground/60 text-xs">{iconDropdownOpen ? "▲" : "▼"}</span>
                    </button>
                    {iconDropdownOpen && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-card border border-border shadow-xl">
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                          <Search className="h-3.5 w-3.5 text-muted-foreground/60" />
                          <input
                            type="text"
                            value={iconSearch}
                            onChange={(e) => setIconSearch(e.target.value)}
                            placeholder="Search icons..."
                            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
                            autoFocus
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {MATERIAL_ICONS.filter((i) => i.toLowerCase().includes(iconSearch.toLowerCase())).map((i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => { setForm({ ...form, icon: i }); setIconDropdownOpen(false); setIconSearch(""); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted transition-colors ${form.icon === i ? "text-volt bg-volt/5" : "text-foreground/80"}`}
                            >
                              <AmenityIcon name={i} className="h-4 w-4" />
                              <span>{i}</span>
                            </button>
                          ))}
                          {MATERIAL_ICONS.filter((i) => i.toLowerCase().includes(iconSearch.toLowerCase())).length === 0 && (
                            <div className="px-3 py-4 text-xs text-muted-foreground text-center">No icons found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">Type</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt">
                    <option value="amenity">Amenity</option>
                    <option value="service">Service</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt">
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeDialog}
                  className="flex-1 border border-border text-foreground/80 px-4 py-2.5 text-xs tracking-widest uppercase font-bold hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-volt text-black font-bold text-xs tracking-widest uppercase px-4 py-2.5 hover:bg-volt/90 transition-colors"
                >
                  {editing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
